import { sum } from 'ramda';
import React, { useMemo } from 'react';
import InputMask from 'react-input-mask';
import styled from 'styled-components';
import Button from '../components/button';
import LoadingTicketsIcon from '../loading-tickets-icon';
import barcodeHint from './barcode-hint.svg';
import { withRootTestId } from '@williamhill/rosi-ui-shared/src/lib';
import { Text, robotoFonts } from '@williamhill/rosi-ui-shared/src/components/typography';

const withTestId = withRootTestId('qa-bet-tracker-manual-entry-ticket-entry');

export const SECTION_LENGTH = [6, 4, 4, 4];

export interface TicketEntryProps {
  value: string | undefined;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onClear: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  error?: string;
}

export default function TicketEntry({
  value,
  isLoading = false,
  error,
  onChange,
  onSubmit,
  onClear,
  onCancel
}: TicketEntryProps) {
  const { expectedLength, mask } = useMemo(calculateExpectedLengthAndFormatMask, []);
  const instruction = `Enter the ${expectedLength} digit ticket number from the front of your ticket`;
  const hasError = error !== undefined;

  return (
    <>
      <Header as="header" weight="bold" size="lg" hasError={hasError} disabled={isLoading}>
        Ticket Number
      </Header>
      <Main>
        <InputMask onChange={({ target: { value } }) => onChange(value)} mask={mask} maskChar="" value={value}>
          {() => (
            <InputContainer>
              <Input
                data-testid={withTestId('input')}
                hasError={hasError}
                autoFocus
                inputMode="numeric"
                placeholder="Enter Number Here"
                type="text"
                disabled={isLoading}
              />
              {!isLoading && (
                <ClearButton as="button" weight="regular" size="lg" onClick={onClear} data-testid={withTestId('clear')}>
                  Clear
                </ClearButton>
              )}
            </InputContainer>
          )}
        </InputMask>
        {!isLoading && (
          <>
            <InputFooter as="footer" size="sm" data-testid={withTestId('instruction')} isError={hasError}>
              {error ?? instruction}
            </InputFooter>
            <Button
              dataTestId={withTestId('submit')}
              disabled={!value || value.length < mask.length}
              onClick={onSubmit}
              text="Search"
            />
          </>
        )}
      </Main>
      {isLoading ? (
        <LoadingContent>
          <LoadingTicketsIcon data-testid={withTestId('loading-indicator')} />
          <Separator />
          <Button
            dataTestId={withTestId('cancel')}
            disabled={!isLoading}
            onClick={onCancel}
            text="Cancel"
            modifiers="secondary"
          />
        </LoadingContent>
      ) : (
        <BarcodeHintImg data-testid={withTestId('barcode-hint')} src={barcodeHint} />
      )}
    </>
  );
}

const BarcodeHintImg = styled.img`
  margin: 3.8rem auto;
  height: 19rem;
  width: 100%;
`;

function calculateExpectedLengthAndFormatMask() {
  return {
    expectedLength: sum(SECTION_LENGTH),
    mask: SECTION_LENGTH.map(sectionLength => '9'.repeat(sectionLength)).join('-')
  };
}

const Separator = styled.div`
  margin-top: 7.7rem;
`;

const LoadingContent = styled.div`
  display: flex;
  padding-top: 8.1rem;
  flex-direction: column;
  align-items: center;
`;

const Header = styled(Text)<{ hasError: boolean; disabled: boolean }>`
  align-self: start;
  ${({ theme, hasError }) => hasError && `color: ${theme.colors.status.error};`}
  ${({ disabled, theme }) => disabled && `color: ${theme.colors.grey400};`}
`;

const Main = styled.main`
  padding-top: 0.7rem;
`;

const ClearButton = styled(Text)`
  position: absolute;
  cursor: pointer;
  background: none;
  border: none;
  right: 0;
  top: 10px;
  color: ${({ theme }) => theme.colors.modals.error};
`;

const Input = styled.input<{ hasError: boolean }>`
  border: none;
  border-bottom: 1px solid;
  font-size: ${robotoFonts.lg};
  padding: 0.8rem 0;
  width: 100%;
  ${({ theme, hasError }) => hasError && `color: ${theme.colors.status.error}`};
  &::placeholder {
    ${({ theme, hasError }) => hasError && `color: ${theme.colors.status.error}`};
  }
  background-color: field;
  ${({ disabled, theme }) => disabled && `color: ${theme.colors.grey400};`}
`;

const InputContainer = styled.div`
  position: relative;
`;

const InputFooter = styled(Text)<{ isError: boolean }>`
  padding: 0.6rem 0;
  ${({ theme, isError }) => isError && `color: ${theme.colors.status.error};`}
`;
